<script setup lang="ts">
import { Page, Section } from "@/components/Ui";
import Card from "@/components/Cards/Card.vue";
import { onEvent } from "@/helpers/events";
import { onMounted, computed, ref, onUnmounted, watch } from "vue";
import { cards, type TCard } from "@/config/cards";
import { CardWithAuth, CardToolbar } from "@/components/Cards";
import { BaseAffiliate } from "@/components/Sections";
import {
  BlockForPurchases,
  BlockForWork,
  BlockForAffiliate,
} from "@/components/Blocks";

const { t } = useI18n();

const direction = ref<string | null>(null);
const advCards = computed<any>(() => {
  return cards.filter((card: TCard) => card.type !== "ultima" && !card.hidden);
});
const toolbarCards = computed<TCard[]>(() => {
  return cards.filter((card: { hidden: boolean }) => !card.hidden)
    .sort(function (x, y) {
      return x.type === "ultima" ? -1 : y.type === "ultima" ? 1 : 0;
    })
    .filter((card: { type: string }) => card.type !== "black");
});
const ultimaCard = computed<any>(() => {
  return cards.find((card: { type: string }) => card.type === "ultima");
});

onMounted(() => {
  onEvent("cards page viewed");
});
onNuxtReady(() => {
  // Detect scroll up
  let oldScrollY = window.scrollY;
  window.onscroll = function () {
    if (oldScrollY < window.scrollY) {
      direction.value = "down";
    } else {
      direction.value = "up";
    }
    oldScrollY = window.scrollY;
  };
})
watch(
  () => direction.value,
  (newVal, oldVal) => {
    const root = document.documentElement;
    root.classList.add(`dir-${newVal}`);
    root.classList.remove(`dir-${oldVal}`);
  }
);
onUnmounted(() => {
  const root = document.documentElement;
  root.classList.remove(`dir-${direction.value}`)
  direction.value = null;
});

useHead({
  htmlAttrs: {
    class: "default-container",
  },
});

useSeoMeta({
  title: t("page.cards.seo.title"),
  description: t("page.cards.seo.description"),
  ogTitle: t("page.cards.seo.title"),
  ogDescription: t("page.cards.seo.description")
});
</script>

<template>
  <Page :class="$style.root">
    <CardToolbar
      :cards="toolbarCards"
      :is-down="direction === 'down'"
      :class="`${$style['toolbar']} ${$style[`toolbar-dir-${direction}`]}`"
    />
    <Section>
      <div :class="$style.head">
        <h1 :class="[$style[`title`], $style[`title-${$i18n.locale}`]]">{{ $t("cards.pageTitle") }}</h1>
        <p>
          {{ $t("cards.pageSubTitle") }}
        </p>
      </div>
      <Card
        v-if="cards"
        :id="`#${ultimaCard.type}`"
        :card="ultimaCard"
      />
    </Section>
    <Section :title-class="$style['root-h2']">
      <template #title>
        {{ $t("cards.instantCardIssuance") }}
      </template>
      <CardWithAuth :card="cards[0]" />
    </Section>
    <Section :title-class="$style['root-h2']">
      <template #title>
        {{ $t("cards.advCards") }}
      </template>
      <div
        v-if="advCards"
        :class="$style.adv"
      >
        <Card
          v-for="(card, index) in advCards"
          :id="`#${card.type}`"
          :key="index"
          :card="card"
          :hide-conditions="card.type !== 'ultima'"
        />
      </div>
    </Section>
    <Section :title-class="$style['root-h2']">
      <template #title>
        {{ $t("cards.forAllNeeds") }}
      </template>
      <div :class="$style.features">
        <BlockForPurchases />
        <BlockForWork />
        <BlockForAffiliate />
      </div>
    </Section>
    <BaseAffiliate />
  </Page>
</template>
<style lang="scss" module>
.root {
  @apply flex flex-col gap-20;
  @apply mt-[70px] md:mt-[90px];
  &-h2 {
    @apply font-bold text-[36px] leading-none text-center;
  }
}

.head {
  @apply flex flex-col items-center gap-6 text-neutral-800 text-center;

  & p {
    @apply text-2xl md:w-5/6 lg:w-full;
  }
}
.title {
  @apply font-extrabold leading-none;
  @apply text-[60px];
  @apply leading-[1.2];
  &-en,
&-id,
&-da,
&-et,
&-hu,
&-lt,
&-nb,
&-ro,
&-sl,
&-cs,
&-el,
&-fi,
&-lv,
&-nl,
&-sk,
&-sv,
&-he,
  &-de,
  &-it,
  &-pt,
  &-tr,
  &-es,
  &-fr,
  &-ja,
  &-zh,
  &-th,
  &-pl,
  &-hi,
  &-ar,
  &-bn,
  &-ko,
  &-vi,
  &-sw{
    @apply text-[60px];
    @apply leading-[1.2];
  }
  &-bg {
    @apply text-[52px] xxs:text-[60px];
  }
  &-be,
  &-ua,
  &-ru {
    @apply text-[42px] xxs:text-[50px] xs:text-[56px] md:text-[60px];
  }
}
.adv {
  @apply flex flex-col gap-[10px];
}

.features {
  @apply grid grid-cols-1 lg:grid-cols-3 gap-x-8 xl:gap-x-10 gap-y-[10px] w-full;
}

.toolbar {
  @apply transition-all transform-gpu w-full md:sticky bg-white sm:backdrop-blur-md sm:bg-opacity-[0.85];

  &-dir {
    &-up {
      @apply md:top-[70px] z-[1000];
    }

    &-down,
    &-null {
      @apply top-0 md:z-[1100];
    }
  }
}
</style>
